import React from "react";
import ReactDOM from "react-dom/client";

import captureException from "./helpers/sentry";
import "./index.css";
import "./sentryInit";

const BOOTSTRAP_RETRY_KEY = "realaml:user-web-app:bootstrap-retry-count";
const MAX_BOOTSTRAP_RETRIES = 2;
const RECOVERABLE_BOOTSTRAP_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Failed to load module script/i,
  /Loading chunk [\w-]+ failed/i,
];

const isRecoverableBootstrapLoadError = (error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);

  return RECOVERABLE_BOOTSTRAP_ERROR_PATTERNS.some((pattern) =>
    pattern.test(message),
  );
};

const clearBootstrapRetry = () => {
  try {
    sessionStorage.removeItem(BOOTSTRAP_RETRY_KEY);
  } catch {
    // Storage can be unavailable in some embedded browsers.
  }
};

const retryBootstrapLoad = (error: unknown) => {
  if (
    !isRecoverableBootstrapLoadError(error) ||
    window.navigator.onLine === false
  ) {
    return false;
  }

  try {
    const retryCount = Number(
      sessionStorage.getItem(BOOTSTRAP_RETRY_KEY) ?? "0",
    );
    if (Number.isNaN(retryCount) || retryCount >= MAX_BOOTSTRAP_RETRIES) {
      return false;
    }

    // Reload a limited number of times to recover from stale hashed asset references after deploys.
    sessionStorage.setItem(BOOTSTRAP_RETRY_KEY, String(retryCount + 1));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
};

const renderBootstrapFailure = () => {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    return;
  }

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <div
        style={{
          padding: "24px",
          textAlign: "center",
          color: "#1f2937",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        Something went wrong. Please refresh and try again.
      </div>
    </React.StrictMode>,
  );
};

const start = async () => {
  const [{ default: i18n }, { default: Router }] = await Promise.all([
    import("./i18n"),
    import("./App"),
  ]);
  await i18n.loadNamespaces(["translation"]);

  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Missing #root element");
  }

  clearBootstrapRetry();
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <Router />
    </React.StrictMode>,
  );
};

const handleBootstrapFailure = (error: unknown) => {
  if (retryBootstrapLoad(error)) {
    return;
  }

  captureException(error);
  renderBootstrapFailure();
};

void start().catch(handleBootstrapFailure);
